import { SelectedWorkspace } from "@/components/molecules/SelectedWorkspace";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { KeyboardEvent, forwardRef } from "react";

interface SearchInputProps {
  searchQuery: string;
  selectedLink?: string | null;
  placeholder?: string;
  disabled?: boolean;
  onSearchChange: (query: string) => void;
  onFocus: () => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onRemoveSelection?: () => void;
  onRemoveSelectionWithDropdown?: () => void;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      searchQuery,
      selectedLink = null,
      placeholder = "Search pages, posts and ideas...",
      disabled = false,
      onSearchChange,
      onFocus,
      onKeyDown,
      onRemoveSelection,
      onRemoveSelectionWithDropdown,
    },
    ref,
  ) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      // Remove selected workspace on Backspace/Delete when input is empty.
      // Only applies to callers that pass selectedLink (e.g. the setup wizard's
      // URL picker) — the dashboard search bar no longer uses this.
      if (
        (e.key === "Backspace" || e.key === "Delete") &&
        searchQuery === "" &&
        selectedLink
      ) {
        e.preventDefault();
        onRemoveSelectionWithDropdown?.();
      }

      onKeyDown?.(e);
    };

    return (
      <div className="relative h-10">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10" />

        <div className="flex items-center w-full h-full rounded-md border border-input px-3 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 min-h-[40px] overflow-hidden">
          {/* padding to align after icon */}
          <div className="w-7 ps-4" />

          {/* selected workspace pill (setup-wizard URL picker only) */}
          {selectedLink && onRemoveSelection && onRemoveSelectionWithDropdown && (
            <SelectedWorkspace
              selectedLink={selectedLink}
              onRemove={onRemoveSelection}
              onRemoveWithDropdown={onRemoveSelectionWithDropdown}
            />
          )}

          {/* input */}
          <Input
            ref={ref}
            type="text"
            placeholder={
              disabled
                ? "Select a website to search"
                : selectedLink
                  ? ""
                  : placeholder
            }
            disabled={disabled}
            className="border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-full ml-1 text-base sm:text-sm shadow-none bg-transparent outline-none rounded-none disabled:cursor-not-allowed"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={onFocus}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>
    );
  },
);

SearchInput.displayName = "SearchInput";
